import { useCallback, useEffect, useState } from 'react';
import {
    AlertTriangle,
    CheckCircle2,
    Clock,
    Loader2,
    RefreshCw,
    X,
} from 'lucide-react';
import useWorkflow from './useWorkflow';
import {
    WorkflowRunSummary,
    WorkflowRunDetail,
    WorkflowRunStatus,
} from './types';

interface RunHistoryPanelProps {
    activationId: number;
    onClose: () => void;
}

const STATUS_FILTERS: { value: WorkflowRunStatus | 'all'; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'completed', label: 'Completed' },
    { value: 'failed', label: 'Failed' },
    { value: 'running', label: 'Running' },
    { value: 'pending', label: 'Pending' },
];

function StatusBadge({ status }: { status: WorkflowRunStatus }) {
    const map: Record<WorkflowRunStatus, { cls: string; icon: JSX.Element }> = {
        completed: {
            cls: 'bg-emerald-100 text-emerald-700',
            icon: <CheckCircle2 size={12} />,
        },
        failed: {
            cls: 'bg-red-100 text-red-700',
            icon: <AlertTriangle size={12} />,
        },
        running: {
            cls: 'bg-blue-100 text-blue-700',
            icon: <Loader2 size={12} className="animate-spin" />,
        },
        pending: {
            cls: 'bg-gray-100 text-gray-600',
            icon: <Clock size={12} />,
        },
    };
    const { cls, icon } = map[status];
    return (
        <span
            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${cls}`}
        >
            {icon}
            {status}
        </span>
    );
}

export default function RunHistoryPanel({
    activationId,
    onClose,
}: RunHistoryPanelProps) {
    const { listRuns, getRunDetail } = useWorkflow();
    const [runs, setRuns] = useState<WorkflowRunSummary[]>([]);
    const [total, setTotal] = useState(0);
    const [filter, setFilter] = useState<WorkflowRunStatus | 'all'>('all');
    const [isLoading, setIsLoading] = useState(true);
    const [selected, setSelected] = useState<WorkflowRunDetail | null>(null);
    const [isLoadingDetail, setIsLoadingDetail] = useState(false);

    const load = useCallback(async () => {
        setIsLoading(true);
        try {
            const result = await listRuns(activationId, {
                status: filter === 'all' ? undefined : filter,
                limit: 50,
            });
            setRuns(result.runs);
            setTotal(result.total);
        } finally {
            setIsLoading(false);
        }
    }, [activationId, filter, listRuns]);

    useEffect(() => {
        load();
    }, [load]);

    const openDetail = async (runId: string) => {
        setIsLoadingDetail(true);
        try {
            setSelected(await getRunDetail(activationId, runId));
        } finally {
            setIsLoadingDetail(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
            <div className="flex h-[80vh] w-[900px] overflow-hidden rounded-lg bg-white shadow-xl">
                {/* List */}
                <div className="flex w-[360px] flex-col border-r border-gray-200">
                    <div className="flex items-center justify-between border-b border-gray-200 p-3">
                        <span className="text-sm font-semibold text-gray-800">
                            Run history{' '}
                            <span className="text-gray-400">({total})</span>
                        </span>
                        <button type="button" onClick={load} title="Refresh">
                            <RefreshCw size={15} className="text-gray-500" />
                        </button>
                    </div>
                    <div className="flex flex-wrap gap-1 border-b border-gray-200 p-2">
                        {STATUS_FILTERS.map((f) => (
                            <button
                                key={f.value}
                                type="button"
                                onClick={() => setFilter(f.value)}
                                className={`rounded-full px-2 py-0.5 text-[11px] ${
                                    filter === f.value
                                        ? 'bg-blue-600 text-white'
                                        : 'bg-gray-100 text-gray-600'
                                }`}
                            >
                                {f.label}
                            </button>
                        ))}
                    </div>
                    <div className="flex-1 overflow-auto">
                        {isLoading ? (
                            <div className="flex items-center gap-2 p-4 text-sm text-gray-500">
                                <Loader2 className="animate-spin" size={16} />{' '}
                                Loading…
                            </div>
                        ) : runs.length === 0 ? (
                            <div className="p-4 text-sm text-gray-400">
                                No runs yet.
                            </div>
                        ) : (
                            runs.map((run) => (
                                <button
                                    key={run.id}
                                    type="button"
                                    onClick={() => openDetail(run.id)}
                                    className={`block w-full border-b border-gray-100 p-3 text-left hover:bg-gray-50 ${
                                        selected?.id === run.id
                                            ? 'bg-blue-50'
                                            : ''
                                    }`}
                                >
                                    <div className="flex items-center justify-between">
                                        <StatusBadge status={run.status} />
                                        <span className="text-[11px] text-gray-400">
                                            {new Date(
                                                run.createdAt,
                                            ).toLocaleString()}
                                        </span>
                                    </div>
                                    <div className="mt-1 truncate font-mono text-[11px] text-gray-600">
                                        {run.inputPreview || '—'}
                                    </div>
                                    {run.error && (
                                        <div className="mt-1 truncate text-[11px] text-red-600">
                                            {run.error}
                                        </div>
                                    )}
                                </button>
                            ))
                        )}
                    </div>
                </div>

                {/* Detail */}
                <div className="flex flex-1 flex-col">
                    <div className="flex items-center justify-between border-b border-gray-200 p-3">
                        <span className="text-sm font-semibold text-gray-800">
                            {selected ? 'Run detail' : 'Select a run'}
                        </span>
                        <button type="button" onClick={onClose}>
                            <X size={18} className="text-gray-500" />
                        </button>
                    </div>
                    <div className="flex-1 overflow-auto p-4 text-xs">
                        {isLoadingDetail ? (
                            <div className="flex items-center gap-2 text-gray-500">
                                <Loader2 className="animate-spin" size={16} />{' '}
                                Loading…
                            </div>
                        ) : !selected ? (
                            <div className="text-gray-400">
                                Pick a run on the left to inspect its steps,
                                input, and output.
                            </div>
                        ) : (
                            <RunDetailView detail={selected} />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function RunDetailView({ detail }: { detail: WorkflowRunDetail }) {
    return (
        <div className="space-y-3">
            <div className="flex items-center gap-2">
                <StatusBadge status={detail.status} />
                <span className="text-gray-400">
                    {new Date(detail.createdAt).toLocaleString()}
                </span>
            </div>

            {detail.error && (
                <div className="rounded bg-red-50 p-2 text-red-700">
                    {detail.error}
                </div>
            )}

            <div>
                <div className="mb-1 font-semibold text-gray-700">Input</div>
                <pre className="overflow-auto rounded bg-gray-100 p-2 text-gray-700">
                    {detail.input ?? '—'}
                </pre>
            </div>

            <div>
                <div className="mb-1 font-semibold text-gray-700">Steps</div>
                <ol className="space-y-2">
                    {detail.trace.map((step, i) => (
                        <li
                            key={`${step.nodeId}-${i}`}
                            className={`rounded-md border p-2 ${
                                step.error
                                    ? 'border-red-300 bg-red-50'
                                    : 'border-gray-200'
                            }`}
                        >
                            <div className="flex items-center justify-between">
                                <span className="font-medium text-gray-800">
                                    {step.nodeKey}{' '}
                                    <span className="text-gray-400">
                                        ({step.type})
                                    </span>
                                </span>
                                {step.branchTaken && (
                                    <span className="rounded-full bg-purple-100 px-2 py-0.5 text-[10px] text-purple-700">
                                        → {step.branchTaken}
                                    </span>
                                )}
                            </div>
                            {step.error ? (
                                <div className="mt-1 text-red-600">
                                    {step.error}
                                </div>
                            ) : (
                                step.output !== undefined && (
                                    <pre className="mt-1 max-h-40 overflow-auto rounded bg-gray-900 p-2 text-[11px] text-emerald-200">
                                        {JSON.stringify(step.output, null, 2)}
                                    </pre>
                                )
                            )}
                        </li>
                    ))}
                </ol>
            </div>

            {detail.status === 'completed' && (
                <div>
                    <div className="mb-1 font-semibold text-gray-700">
                        Output
                    </div>
                    <pre className="overflow-auto rounded bg-gray-900 p-2 text-emerald-200">
                        {JSON.stringify(detail.output, null, 2)}
                    </pre>
                </div>
            )}
        </div>
    );
}
